import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { mergeOptions, mergeProps } from '@/utils/merge';
import { FrisbiiPayment } from 'base/components/standalone/FrisbiiPayment/FrisbiiPayment';
import Script from 'next/script';
import { ComponentProps, useCallback, useEffect, useState } from 'react';
import { useFrisbiiPayment } from '@/hooks/useFrisbiiPayment';
import useUserAuth from '@/hooks/useUserAuth';
import { tryGetWindowProp } from '@/utils/tryGetWindowProp';

export interface StandaloneFrisbiiPaymentProps extends StandaloneComponentProps<typeof FrisbiiPayment> {
  button?: ComponentProps<typeof FrisbiiPayment.Button>;
  handleSuccess?: (successResponse: { redirect_to: string }) => Promise<any>;
  handleFailure?: (message?: string, error?: any) => Promise<any>;
  autoTrigger?: boolean;
}

export const StandaloneFrisbiiPayment: StandaloneComponent<StandaloneFrisbiiPaymentProps> = ({
  button,
  handleSuccess,
  handleFailure,
  autoTrigger,
  ...props
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);
  const { frisbiiSession, onAcceptEvent, onCancelEvent, onErrorFrisbiiEvent, createFrisbiiSession } =
    useFrisbiiPayment();
  const { isUserLoggedIn } = useUserAuth();

  const openFrisbiiPurchaseWindow = useCallback(
    async (session = frisbiiSession) => {
      if (!session) {
        console.warn('Frisbii session is not available');
        return;
      }

      const { sessionId, signature } = session;
      if (!sessionId || !signature) {
        throw new Error('Frisbii session is missing sessionId or signature');
      }

      try {
        const Reepay = await tryGetWindowProp<typeof window.Reepay>('Reepay');

        const modal = new Reepay.ModalSubscription(sessionId, { showReceipt: false });

        modal.addEventHandler(Reepay.Event.Accept, async (data) => {
          try {
            const acceptResponse = await onAcceptEvent({
              from: window.location.href,
              signature: signature,
              ...data,
            });
            handleSuccess?.(acceptResponse);
          } catch (error) {
            console.error('Error during payment acceptance:', error);
            await handleFailure?.();
          } finally {
            modal.destroy();
            setIsProcessing(false);
          }
        });

        modal.addEventHandler(Reepay.Event.Cancel, async (data) => {
          try {
            await onCancelEvent({ from: window.location.href, signature, ...data });
            handleFailure?.('Betalning avbruten av användaren');
          } catch (error) {
            console.error('Error during payment cancellation:', error);
          } finally {
            modal.destroy();
            setIsProcessing(false);
          }
        });

        modal.addEventHandler(Reepay.Event.Error, async (data) => {
          await onErrorFrisbiiEvent(data);
          await handleFailure?.('Betalning avbruten');
        });
      } catch (error) {
        console.error('Error starting Reepay payment:', error);
        await handleFailure?.('Förlåt, ett fel har uppstått. Vänligen försök igen om en stund.');
      }
    },
    [frisbiiSession, onAcceptEvent, onCancelEvent, isProcessing, handleSuccess, handleFailure],
  );

  const startPurchase = useCallback(async () => {
    setIsProcessing(true);
    try {
      let sessionToUse = frisbiiSession;
      if (isUserLoggedIn && !frisbiiSession) {
        sessionToUse = await createFrisbiiSession();
      }

      await openFrisbiiPurchaseWindow(sessionToUse);
    } catch (error) {
      console.error('Error starting purchase process:', error);
    }
    setIsProcessing(false);
  }, [isUserLoggedIn, frisbiiSession]);

  useEffect(() => {
    if (autoTrigger && !hasAutoTriggered && !isProcessing) {
      setHasAutoTriggered(true);
      startPurchase();
    }
  }, [autoTrigger, hasAutoTriggered, isProcessing, openFrisbiiPurchaseWindow]);

  return (
    <FrisbiiPayment {...props}>
      <Script
        key="frisbii-payment"
        id="checkout-script"
        src="https://checkout.reepay.com/checkout.js"
        onError={(error) => {
          console.error('Error loading Reepay checkout script:', error);
          handleFailure?.('Kunde inte ladda betalningssystemet. Vänligen uppdatera sidan.');
        }}
      />
      <FrisbiiPayment.Content>
        <FrisbiiPayment.Button
          {...mergeProps(button, { isLoading: isProcessing })}
          options={mergeOptions(
            {
              onClick: async (e: Event) => {
                e.preventDefault();
                await startPurchase();
              },
              disabled: isProcessing,
            },
            props.options?.$button,
          )}
        />
      </FrisbiiPayment.Content>
    </FrisbiiPayment>
  );
};
